import React from 'react';
import { useDispatch } from 'react-redux';
import { NavLink, useRouteMatch, useLocation } from 'react-router-dom';
import { Box, Flex, Text } from 'rebass/styled-components';
import { CircleArrowUp } from 'lucide-react';
import PropTypes from 'prop-types';
import { Badge, Tooltip } from '@getro/rombo';
import { toggleMenu } from '../../../features/mainMenu';

export const MenuNav = ({
  path,
  href,
  label,
  icon,
  slug,
  type = 'internal',
  onClick,
  isBeta,
  betaVariant,
  showUpgradeIcon,
  tooltip,
  badge,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const match = useRouteMatch(`/networks/${slug}/list`);

  const link = (
    <Box
      as={type === 'external' ? 'a' : NavLink}
      href={type === 'external' ? href : undefined}
      to={type === 'external' ? undefined : path}
      target={type === 'external' ? '_blank' : undefined}
      className={({ isActive }) => (isActive || (label === 'Lists' && !!match) ? 'active' : '')}
      style={{ textDecoration: 'none' }}
      onClick={(e) => {
        const jobsPath = `/networks/${slug}/jobs`;
        if (path === jobsPath && location.pathname === jobsPath) {
          e.preventDefault();
        }

        const width = window.screen.availWidth < window.innerWidth ? window.screen.availWidth : window.innerWidth;

        if (width < 990) {
          dispatch(toggleMenu());
        }
        onClick?.();
      }}
    >
      <Flex
        tx="mainMenu"
        variant="item"
        alignItems="center"
        sx={{ gap: '8px', borderRadius: 'default', flex: 1, minWidth: 0, position: 'relative' }}
      >
        {icon && (
          <Box
            as={icon}
            className="menu-item-icon"
            width="16px"
            height="16px"
            aria-hidden="true"
            flex="0 0 auto"
            sx={{ color: 'neutral.400' }}
          />
        )}
        <Text data-testid={`main-menu-${label.toLowerCase()}`} className="main-menu-item" sx={{ flexShrink: 0 }}>
          {label}
        </Text>
        {isBeta && (
          <Text
            sx={{
              fontSize: '10px',
              fontStyle: 'italic',
              fontWeight: '700',
              lineHeight: '16px',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              flexShrink: 0,
              mt: '-4px',
              ml: '-4px',
              ...(betaVariant === 'gradient'
                ? {
                    background: 'linear-gradient(94deg, #4900FF 3.21%, #D400FF 63.82%, #FF9257 134.26%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }
                : {
                    color: 'purple.400',
                  }),
            }}
          >
            BETA
          </Text>
        )}
        {badge && (
          <Badge
            size="small"
            sx={{
              color: 'purple.400',
              backgroundColor: 'purple.100',
              fontWeight: '600',
              boxShadow: 'none',
              fontSize: '10px',
              lineHeight: '16px',
              borderRadius: '4px',
              px: '6px',
            }}
          >
            {badge}
          </Badge>
        )}
        {showUpgradeIcon && (
          <Box
            as={CircleArrowUp}
            className="upgrade-icon"
            width="16px"
            height="16px"
            aria-hidden="true"
            flex="0 0 auto"
            sx={{ color: 'neutral.400', position: 'absolute', right: '4px', backgroundColor: 'neutral.20' }}
          />
        )}
      </Flex>
    </Box>
  );

  return (
    <Box
      key={`${path}-${href}`}
      as="li"
      sx={{
        '> a': {
          '> div': {
            transition: 'background-color 0.3s ease',
            willChange: 'background-color',
          },
          '&:hover': {
            '> div': {
              bg: 'neutral.50',
              color: 'text.dark',
              '.menu-item-icon': {
                color: 'neutral.700',
              },
              '.upgrade-icon': {
                backgroundColor: 'neutral.50',
              },
            },
          },
          '&.active': {
            '> div': {
              bg: 'neutral.50',
              color: 'text.dark',
              '.menu-item-icon': {
                color: 'neutral.700',
              },
              '.upgrade-icon': {
                backgroundColor: 'neutral.50',
              },
            },
          },
        },
      }}
    >
      {tooltip ? (
        <Tooltip
          content={tooltip}
          position="right"
          openWith={['hover']}
          options={{ delayShow: 400, delayHide: 0 }}
          trigger={link}
        />
      ) : (
        link
      )}
    </Box>
  );
};

MenuNav.propTypes = {
  path: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.func,
  slug: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['internal', 'external']),
  onClick: PropTypes.func,
  isBeta: PropTypes.bool,
  betaVariant: PropTypes.oneOf(['gradient', 'flat-purple']),
  showUpgradeIcon: PropTypes.bool,
  tooltip: PropTypes.string,
  badge: PropTypes.string,
};

MenuNav.defaultProps = {
  type: 'internal',
  onClick: () => {},
  isBeta: false,
  betaVariant: 'gradient',
  showUpgradeIcon: false,
  icon: undefined,
  tooltip: undefined,
  badge: null,
};
